import React, { useEffect, useState, useCallback } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Table, Button, Nav, Spinner, Badge, Form } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { getToken, getUserId } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";
import {
  DESIRABLE_LABELS, RTAW_GROUPS, RTAW_LABELS, ITEM_MAX,
  itemsToScore100, ratingFrom100,
} from "../utils/por5Utils";

const TD_FIELDS = Array.from({ length: 8 }, (_, i) => `td_item${i + 1}`);
const RT_FIELDS = Array.from({ length: 15 }, (_, i) => `rt_item${i + 1}`);

const RATING_BADGE = {
  'ดีเยี่ยม': 'success',
  'ดี': 'primary',
  'ผ่านเกณฑ์': 'warning',
  'ไม่ผ่านเกณฑ์': 'danger',
};

// 0-10 numeric cell; empty = not assessed yet
function ScoreInput({ value, onChange }) {
  return (
    <Form.Control
      size="sm"
      type="number"
      min="0"
      max={ITEM_MAX}
      step="1"
      className="text-center px-1"
      style={{ minWidth: 48 }}
      value={value ?? ""}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "") return onChange(null);
        const n = Math.max(0, Math.min(ITEM_MAX, parseInt(v, 10) || 0));
        onChange(String(n));
      }}
    />
  );
}

// การ์ดประเมินนักเรียน 1 คน สำหรับมุมมองมือถือ
const StudentPor5Card = ({ student, index, fields, tab, onScoreChange }) => {
  const values = fields.map((f) => student[f]);
  const score100 = itemsToScore100(values);
  const rating = ratingFrom100(score100);
  const allFilled = values.every((v) => v !== null);
  return (
    <div className={`border rounded p-3 ${student.st_status === "0" ? 'bg-danger-subtle' : allFilled ? 'bg-success-subtle' : ''}`}>
      <div className="fw-semibold">{student.st_name}</div>
      <div className="fs-xs text-muted">#{index + 1} · {student.st_idnumber}</div>
      <div className="mt-2 pt-2 border-top d-flex flex-wrap gap-2">
        {fields.map((f, i) => (
          <div key={f} style={{ width: 60 }}>
            <div className="fs-xs text-muted text-truncate" title={tab === "desirable" ? DESIRABLE_LABELS[i] : RTAW_LABELS[i]}>
              {tab === "desirable" ? `${i + 1}.` : `${(i % 5) + 1}`}
            </div>
            <ScoreInput value={student[f]} onChange={(v) => onScoreChange(index, f, v)} />
          </div>
        ))}
      </div>
      <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top fs-sm">
        <div><span className="text-muted">รวม (100):</span> <strong>{score100 ?? "-"}</strong></div>
        <div>{rating ? <Badge bg={RATING_BADGE[rating]}>{rating}</Badge> : "-"}</div>
      </div>
    </div>
  )
}

export default function Por5Assessment() {
  const { teach_id } = useParams();
  const { setLoading } = useLoading();
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (skin) => {
    const tables = document.getElementsByTagName("table");
    for (const table of tables) {
      const theads = table.querySelectorAll("thead");
      if (skin === "dark") {
        for (const thead of theads) { thead.classList.remove("table-secondary"); thead.classList.add("table-primary"); }
        table.classList.add("table-dark");
      } else {
        table.classList.remove("table-dark");
        for (const thead of theads) { thead.classList.remove("table-primary"); thead.classList.add("table-secondary"); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const [tab, setTab] = useState("desirable");
  const [subject, setSubject] = useState("");
  const [room, setRoom] = useState("");
  const [semester, setSemester] = useState("");
  const [data, setData] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  const loadData = useCallback(() => {
    setLoading(true);
    axiosInstance
      .get(`${process.env.REACT_APP_API}/por5/assessment/${teach_id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then((res) => {
        const rows = res.data;
        if (rows.length > 0) {
          const first = rows[0];
          setSubject(`${first.sub_subid} ${first.sub_subname}`);
          setRoom(`${first.tb_class_c_id}/${first.r_no}`);
          setSemester(first.sub_semester);
        }
        setData(rows.map((r) => {
          const row = {
            study_id: r.study_id,
            st_idnumber: r.st_idnumber,
            st_name: r.st_name,
            st_status: r.st_status,
          };
          [...TD_FIELDS, ...RT_FIELDS].forEach((f) => {
            row[f] = r[f] != null ? String(r[f]) : null;
          });
          return row;
        }));
        setDirty(false);
      })
      .catch((err) => {
        if (err.response?.status !== 404) {
          Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: err.response?.data?.message || err.message });
        }
        setData([]);
      })
      .finally(() => setLoading(false));
  }, [teach_id, setLoading]);

  useEffect(() => { loadData(); }, [loadData]);

  const fields = tab === "desirable" ? TD_FIELDS : RT_FIELDS;

  const handleScoreChange = (index, field, value) => {
    setData((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setDirty(true);
  };

  // Fill one column for every active student (skips withdrawn)
  const handleFillColumn = (field) => {
    Swal.fire({
      title: "กรอกทั้งคอลัมน์",
      input: "number",
      inputAttributes: { min: 0, max: ITEM_MAX, step: 1 },
      inputLabel: `คะแนน 0-${ITEM_MAX} (เว้นว่าง = ล้างคอลัมน์)`,
      showCancelButton: true,
      confirmButtonText: "กรอก",
      cancelButtonText: "ยกเลิก",
    }).then((res) => {
      if (!res.isConfirmed) return;
      const v = res.value === "" ? null : String(Math.max(0, Math.min(ITEM_MAX, parseInt(res.value, 10) || 0)));
      setData((prev) => prev.map((s) => (s.st_status === "0" ? s : { ...s, [field]: v })));
      setDirty(true);
    });
  };

  const handleSave = () => {
    setSaving(true);
    const endpoint = tab === "desirable" ? "saveDesirable" : "saveRtaw";
    const saveFields = tab === "desirable" ? TD_FIELDS : RT_FIELDS;
    axiosInstance
      .post(`${process.env.REACT_APP_API}/por5/${endpoint}`, {
        teach_id,
        update_by: String(getUserId()),
        students: data.map((s) => {
          const out = { study_id: s.study_id };
          saveFields.forEach((f) => { out[f] = s[f] !== null ? Number(s[f]) : null; });
          return out;
        }),
      }, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(() => {
        setDirty(false);
        Swal.fire({ title: "บันทึกสำเร็จ", icon: "success", timer: 1200, showConfirmButton: false });
      })
      .catch((err) => {
        Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: err.response?.data?.message || err.message });
      })
      .finally(() => setSaving(false));
  };

  const countFilled = data.filter((s) => fields.every((f) => s[f] !== null)).length;

  const switchTab = (k) => {
    if (k === tab) return;
    if (dirty && !window.confirm("มีข้อมูลที่ยังไม่ได้บันทึก ต้องการเปลี่ยนแท็บหรือไม่?")) return;
    setTab(k);
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/teaching/saveGrade">การสอน</Link></li>
              <li className="breadcrumb-item active">ประเมิน ปพ.5</li>
            </ol>
            <h4 className="main-title mb-0">ประเมินคุณลักษณะฯ / อ่าน คิดวิเคราะห์ เขียน (ปพ.5)</h4>
            <p className="text-muted mb-0 fs-sm">
              {subject} ห้อง {room} ภาคเรียนที่ {semester}
            </p>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <Link to={`/teaching/por5-book/${teach_id}`} className="btn btn-white btn-sm">
              <i className="ri-printer-line me-1"></i>พิมพ์เล่ม ปพ.5
            </Link>
          </div>
        </div>

        <Row className="g-12 mb-4">
          <Col xl="12">
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <Nav variant="tabs" activeKey={tab} onSelect={switchTab}>
                    <Nav.Item><Nav.Link eventKey="desirable">คุณลักษณะอันพึงประสงค์</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="rtaw">อ่าน คิดวิเคราะห์ เขียน</Nav.Link></Nav.Item>
                  </Nav>
                  <small className="text-muted">
                    ประเมินแล้ว <Badge bg="success">{countFilled}</Badge> / {data.length} คน
                  </small>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                    <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                      onClick={() => setView('table')} title="มุมมองตาราง">
                      <i className="ri-table-line" />
                    </button>
                    <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                      onClick={() => setView('card')} title="มุมมองการ์ด">
                      <i className="ri-layout-grid-line" />
                    </button>
                  </div>
                  <Button
                    variant={dirty ? "warning" : "success"}
                    size="sm"
                    onClick={handleSave}
                    disabled={saving || data.length === 0}
                  >
                    {saving
                      ? <Spinner animation="border" size="sm" />
                      : <><i className="ri-save-3-fill me-1"></i>บันทึกทั้งหมด</>}
                  </Button>
                </div>
              </Card.Header>

              <Card.Body className="p-2" style={{ overflowX: "auto" }}>
                <div className="d-flex gap-3 mb-2 ms-1 text-muted" style={{ fontSize: "12px" }}>
                  <span>คะแนน 0-{ITEM_MAX} ต่อข้อ รวมคิดเป็นคะแนนเต็ม 100</span>
                  <span>เกณฑ์: ≥85 ดีเยี่ยม, ≥70 ดี, ≥50 ผ่านเกณฑ์</span>
                  {view === 'table' && <span>( คลิกหัวคอลัมน์เพื่อกรอกทั้งคอลัมน์ )</span>}
                </div>

                {data.length === 0 ? (
                  <div className="text-center text-muted py-4">ไม่พบข้อมูลนักเรียน</div>
                ) : view === 'card' ? (
                  <Row className="g-3">
                    {data.map((student, index) => (
                      <Col key={student.study_id} xs={12} md={6} xl={4}>
                        <StudentPor5Card student={student} index={index} fields={fields} tab={tab}
                          onScoreChange={handleScoreChange} />
                      </Col>
                    ))}
                  </Row>
                ) : (
                <Table responsive bordered hover size="sm" className="mb-0 align-middle" style={{ minWidth: tab === "rtaw" ? 1500 : 1100 }}>
                  <thead>
                    {tab === "rtaw" && (
                      <tr className="text-center">
                        <th colSpan={3}></th>
                        {RTAW_GROUPS.map((g) => (
                          <th key={g.name} colSpan={g.count}>{g.name}</th>
                        ))}
                        <th colSpan={2}></th>
                      </tr>
                    )}
                    <tr className="text-center align-middle">
                      <th style={{ width: 40 }}>ที่</th>
                      <th style={{ width: 90 }}>เลขประจำตัว</th>
                      <th style={{ minWidth: 160, textAlign: "left" }}>ชื่อ-สกุล</th>
                      {fields.map((f, i) => (
                        <th
                          key={f}
                          style={{ width: 60, fontSize: "11px", lineHeight: 1.2, cursor: "pointer" }}
                          title={`${tab === "desirable" ? DESIRABLE_LABELS[i] : RTAW_LABELS[i]} — คลิกเพื่อกรอกทั้งคอลัมน์`}
                          onClick={() => handleFillColumn(f)}
                        >
                          {tab === "desirable" ? <>{i + 1}. {DESIRABLE_LABELS[i]}</> : (i % 5) + 1}
                        </th>
                      ))}
                      <th style={{ width: 60 }}>รวม<br /><small>(100)</small></th>
                      <th style={{ width: 90 }}>ผลประเมิน</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length > 0 ? data.map((student, index) => {
                      const values = fields.map((f) => student[f]);
                      const score100 = itemsToScore100(values);
                      const rating = ratingFrom100(score100);
                      const allFilled = values.every((v) => v !== null);
                      return (
                        <tr key={student.study_id} className={student.st_status === "0" ? "table-danger" : allFilled ? "table-success" : ""}>
                          <td className="text-center">{index + 1}</td>
                          <td className="text-center">{student.st_idnumber}</td>
                          <td>{student.st_name}</td>
                          {fields.map((f) => (
                            <td key={f} className="text-center p-1">
                              <ScoreInput
                                value={student[f]}
                                onChange={(v) => handleScoreChange(index, f, v)}
                              />
                            </td>
                          ))}
                          <td className="text-center fw-semibold">{score100 ?? "-"}</td>
                          <td className="text-center">
                            {rating ? <Badge bg={RATING_BADGE[rating]}>{rating}</Badge> : "-"}
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr><td colSpan={fields.length + 5} className="text-center text-muted py-4">ไม่พบข้อมูลนักเรียน</td></tr>
                    )}
                  </tbody>
                </Table>
                )}

                {dirty && (
                  <div className="d-flex justify-content-end mt-2">
                    <Button variant="warning" size="sm" onClick={handleSave} disabled={saving}>
                      {saving ? <Spinner animation="border" size="sm" /> : <><i className="ri-save-3-fill me-1"></i>บันทึกทั้งหมด</>}
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </React.Fragment>
  );
}
